import React from "react";
import { Button, Flex } from "@jsq/ds/core";
import type { TaskStepDetail } from "../../types";

interface MakerActionBarProps {
  task: TaskStepDetail;
  actionLoading: boolean;
  allSubtasksCompleted: boolean;
  /**
   * True when the task is unassigned OR assigned to someone other than the
   * logged-in user. Submit must be inert in that case — the BE would reject
   * the mutation and the UX shouldn't invite the click.
   */
  isNotAssignee?: boolean;
  isDocumentUploading?: boolean;
  onActionClick: (action: string) => void;
}

// BE may send either start_work (NOT_STARTED → IN_PROGRESS, label "Start") or
// submit_for_review (IN_PROGRESS → IN_REVIEW). The maker-side UX renders both
// as a single "Submit for review" primary button — the FE doesn't expose the
// intermediate transition to the user. When the task hasn't been started yet,
// emit the composite "start_and_submit" intent so a single click chains
// start_work → submit_for_review and lands the task in IN_REVIEW; firing only
// start_work would leave the task stuck IN_PROGRESS and re-firing it errors
// with "Cannot transition node from IN_PROGRESS to IN_PROGRESS".
const SUBMIT_ACTIONS = new Set(["start_work", "submit_for_review"]);

export const MakerActionBar: React.FC<MakerActionBarProps> = ({
  task,
  actionLoading,
  allSubtasksCompleted,
  isNotAssignee = false,
  isDocumentUploading = false,
  onActionClick,
}) => {
  const submitAction = task.availableActions.find((a) => SUBMIT_ACTIONS.has(a.action));

  if (!submitAction) return null;

  const isSubmitDisabled =
    actionLoading || !allSubtasksCompleted || isNotAssignee || isDocumentUploading;
  const submitIntent =
    submitAction.action === "start_work" ? "start_and_submit" : "submit_for_review";

  return (
    <Flex gap="8px" alignItems="center">
      <Button
        variant="primary"
        disabled={isSubmitDisabled}
        onClick={() => onActionClick(submitIntent)}
        data-testid="maker-submit-for-review"
      >
        Submit for review
      </Button>
    </Flex>
  );
};

export default MakerActionBar;
